import { CheckedAddress } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { stringToHex } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { delegateRegistryConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { spkGovernanceSnapshotSpaceName } from '@/features/spk/governance/config'

export interface DelegateQueryParams {
  delegator: CheckedAddress
  chainId: number
  wagmiConfig: Config
}

export type DelegateQueryData = {
  delegate: CheckedAddress | undefined
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function delegateQueryOptions({ delegator, chainId, wagmiConfig }: DelegateQueryParams) {
  return queryOptions({
    queryKey: delegateQueryKey({ delegator, chainId }),
    queryFn: () => delegateQueryFunction({ delegator, chainId, wagmiConfig }),
  })
}

export function delegateQueryKey({ delegator, chainId }: Pick<DelegateQueryParams, 'delegator' | 'chainId'>): QueryKey {
  return ['delegate', chainId, delegator]
}

export async function delegateQueryFunction({
  delegator,
  wagmiConfig,
  chainId,
}: DelegateQueryParams): Promise<DelegateQueryData> {
  const delegate = await readContract(wagmiConfig, {
    address: getContractAddress(delegateRegistryConfig.address, chainId),
    abi: delegateRegistryConfig.abi,
    functionName: 'delegation',
    args: [delegator, stringToHex(spkGovernanceSnapshotSpaceName, { size: 32 })],
    chainId,
  })

  if (delegate === CheckedAddress.ZERO()) {
    return { delegate: undefined }
  }

  return { delegate: CheckedAddress(delegate) }
}
